import React from "react";
import { Button, Input, Modal, Segmented, Space, Switch } from "antd";
import { DeleteOutlined, PlusOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Game } from "../../../../services/gameQuiz/gameQuiz.types";
import ImageUploadInput from "../ImageUploadInput";
import { generateOptionId, tokenizeWords } from "../gameTypes";
import TtsAudioControls from "../TtsAudioControls";

type TextSelectionGame = Extract<Game, { type: "text_selection" }>;
type Part = TextSelectionGame["parts"][number];

type Props = {
  game: TextSelectionGame;
  onChange: (game: TextSelectionGame) => void;
  generateAudio?: (text: string) => Promise<string>;
};

const TextSelectionEditor: React.FC<Props> = ({
  game,
  onChange,
  generateAudio,
}) => {
  // Defensivo: juegos parciales/legacy podrían llegar sin estos campos.
  const parts: Part[] = Array.isArray(game.parts) ? game.parts : [];
  const authoringMode = game.authoringMode ?? "words";

  const selectableParts = parts.filter((p) => p.selectable);

  const setParts = (next: Part[]) => onChange({ ...game, parts: next });

  const togglePartCorrect = (partId: string, correct: boolean) =>
    setParts(parts.map((p) => (p.id === partId ? { ...p, correct } : p)));

  // ----- Modo palabras -----
  const handleTokenize = () => {
    if (!game.text.trim()) {
      Modal.warning({
        title: "Escribí la frase primero",
        content: "Necesitás texto en la frase para tokenizarla en palabras.",
      });
      return;
    }
    const next = tokenizeWords(game.text);
    if (selectableParts.length > 0) {
      Modal.confirm({
        title: "Re-tokenizar la frase",
        content:
          "Esto reemplazará las palabras actuales y borrará las marcas de correctas.",
        okText: "Re-tokenizar",
        cancelText: "Cancelar",
        onOk: () => setParts(next),
      });
    } else {
      setParts(next);
    }
  };

  // ----- Modo fragmentos -----
  const addFragment = () => {
    const next: Part[] = [
      ...parts,
      {
        id: generateOptionId(),
        text: "",
        selectable: true,
        correct: false,
        trailing: " ",
      },
    ];
    setParts(normalizeFragmentTrailing(next));
  };

  const updateFragmentText = (partId: string, text: string) =>
    setParts(parts.map((p) => (p.id === partId ? { ...p, text } : p)));

  const removeFragment = (partId: string) =>
    setParts(normalizeFragmentTrailing(parts.filter((p) => p.id !== partId)));

  // En modo fragmentos unimos con un espacio entre fragmentos, sin espacio final.
  const normalizeFragmentTrailing = (list: Part[]): Part[] =>
    list.map((p, i) => ({ ...p, trailing: i < list.length - 1 ? " " : "" }));

  // ----- Cambio de modo -----
  // El cambio de modo es inmediato y NO destructivo: conservamos las partes y
  // solo ajustamos el `trailing` según el modo destino (en fragmentos unimos con
  // un espacio; en palabras dejamos el `trailing` tal cual viene de la frase).
  const handleModeChange = (value: string | number) => {
    const nextMode = value as TextSelectionGame["authoringMode"];
    if (nextMode === authoringMode) return;
    const nextParts =
      nextMode === "fragments" ? normalizeFragmentTrailing(parts) : parts;
    onChange({ ...game, authoringMode: nextMode, parts: nextParts });
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <div>
        <label>Pregunta</label>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Input.TextArea
            value={game.question}
            onChange={(e) => onChange({ ...game, question: e.target.value })}
            rows={2}
            style={{ flex: 1 }}
          />
          <TtsAudioControls
            text={game.question}
            audioUrl={game.questionAudio}
            onAudioChange={(url) => onChange({ ...game, questionAudio: url })}
            generateAudio={generateAudio}
          />
        </div>
      </div>

      <div>
        <label>Imagen (opcional)</label>
        <ImageUploadInput
          value={game.image}
          onChange={(url) => onChange({ ...game, image: url })}
        />
      </div>

      <div>
        <label>Frase (el estudiante seleccionará partes de este texto)</label>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Input.TextArea
            value={game.text}
            onChange={(e) => onChange({ ...game, text: e.target.value })}
            rows={3}
            placeholder="Ej: El gato negro corre rápido por el jardín."
            style={{ flex: 1 }}
          />
          <TtsAudioControls
            text={game.text}
            audioUrl={game.textAudio}
            onAudioChange={(url) => onChange({ ...game, textAudio: url })}
            generateAudio={generateAudio}
          />
        </div>
      </div>

      <div>
        <label style={{ display: "block", marginBottom: 8 }}>
          Modo de selección
        </label>
        <Segmented
          value={authoringMode}
          onChange={handleModeChange}
          options={[
            { label: "Palabras", value: "words" },
            { label: "Fragmentos", value: "fragments" },
          ]}
        />
      </div>

      {authoringMode === "words" ? (
        <div>
          <Button
            icon={<ThunderboltOutlined />}
            onClick={handleTokenize}
            style={{ marginBottom: 8 }}
          >
            Tokenizar en palabras
          </Button>
          {selectableParts.length === 0 ? (
            <p style={{ color: "#999" }}>
              Escribí la frase y pulsá &quot;Tokenizar en palabras&quot; para
              marcar cuáles son correctas.
            </p>
          ) : (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginTop: 8,
              }}
            >
              {selectableParts.map((part) => (
                <div
                  key={part.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 8px",
                    border: "1px solid var(--bo-card-border)",
                    borderRadius: 6,
                    background: part.correct ? "#D1FAE5" : "var(--bo-card-bg)",
                    // The mint highlight is theme-invariant, so its text must
                    // stay dark even when the dark theme text is light.
                    color: part.correct ? "rgba(0, 0, 0, 0.88)" : undefined,
                  }}
                >
                  <span>{part.text}</span>
                  <Switch
                    size="small"
                    checked={part.correct}
                    onChange={(checked) =>
                      togglePartCorrect(part.id, checked)
                    }
                  />
                </div>
              ))}
            </div>
          )}
          <p style={{ color: "#999", marginTop: 8 }}>
            Activá el switch en las palabras que el estudiante debe seleccionar.
          </p>
        </div>
      ) : (
        <div>
          <label>Fragmentos (marcá cuáles son correctos)</label>
          {selectableParts.map((part) => (
            <Space
              key={part.id}
              style={{ display: "flex", marginTop: 8 }}
              align="center"
            >
              <Input
                value={part.text}
                onChange={(e) => updateFragmentText(part.id, e.target.value)}
                placeholder="Texto del fragmento"
                style={{ minWidth: 280 }}
              />
              <span>Correcto</span>
              <Switch
                checked={part.correct}
                onChange={(checked) => togglePartCorrect(part.id, checked)}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeFragment(part.id)}
              />
            </Space>
          ))}
          <div style={{ marginTop: 8 }}>
            <Button
              icon={<PlusOutlined />}
              type="dashed"
              block
              onClick={addFragment}
            >
              Agregar fragmento
            </Button>
          </div>
        </div>
      )}
    </Space>
  );
};

export default TextSelectionEditor;
